<template>
  <div
    class="outreach-contact-sidebar"
    :class="{
      'outreach-contact-sidebar--database-matches-preview':
        isDatabaseMatchesPreview,
    }"
    data-testid="outreach-contact-sidebar"
  >
    <div
      v-if="isDatabaseMatchesPreview"
      class="outreach-contact-sidebar__database-preview-header tw:flex tw:items-center tw:justify-between tw:border-b tw:border-denim-200 tw:py-3"
    >
      <button
        class="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:border-0 tw:bg-transparent tw:p-0 tw:text-sm tw:text-denim-700"
        data-testid="back-to-database-matches"
        type="button"
        @click="backToDatabaseMatches"
      >
        <BaseIcon icon="chevron-left" />
        <span>
          {{
            getLocaleText(
              "modals.outreach_database_matches_panel.back_to_private_contacts"
            )
          }}
        </span>
      </button>
      <BaseButton
        :aria-label="getLocaleText('global.close')"
        circle
        icon="close"
        new-design
        new-icon
        @click="onClose"
      />
    </div>

    <!-- Close button always accessible -->
    <BaseButton
      v-if="!isDatabaseMatchesPreview"
      circle
      class="tw:absolute tw:right-6 tw:top-6 tw:z-[700] tw:text-denim-500"
      icon="close"
      new-design
      @click="onClose"
    />

    <!-- Floating restrict message overlay -->
    <div
      v-if="showRestrictMessage && !isContactLoading"
      class="tw:absolute tw:inset-0 tw:z-[600] tw:flex tw:items-center tw:justify-center tw:pointer-events-none"
      data-testid="restrict-message-container"
    >
      <div class="tw:mx-6 tw:pointer-events-auto">
        <UpsellCard
          :button-label="getLocaleText('upsell_card.get_in_touch')"
          data-testid="outreach-upsell-card"
          :more-info-url="platformUrl"
          origin="outreach-contact-sidebar"
          :types="[AppSection.OUTREACH]"
        />
      </div>
    </div>

    <div
      ref="header"
      class="outreach-contact-sidebar__sticky"
      :class="{
        'tw:blur-sm tw:pointer-events-none tw:select-none': showRestrictMessage,
      }"
    >
      <OutreachContactHeader
        v-if="selectedContact"
        compact
        :contact="selectedContact"
        :disable-actions="isSidebarDisabled"
        :hide-actions="showRestrictMessage"
        :show-skeleton="isContactLoading"
      />

      <BaseSeparator class="outreach-contact-sidebar__separator" />

      <div
        class="outreach-contact-sidebar__tabs"
        data-testid="tabs-container"
      >
        <BaseTabs
          v-model="activeTab"
          data-testid="tabs"
        >
          <BaseTab
            v-for="navTab in tabsFiltered"
            :key="navTab.name"
            :disabled="showRestrictMessage"
            :icon="navTab.icon"
            :label="navTab.label"
            :name="navTab.name"
            new-design
            new-icon
          />
        </BaseTabs>
      </div>
    </div>
    <div
      v-if="isContactLoading"
      class="tw:flex tw:items-center tw:justify-center tw:min-h-[400px] tw:w-full"
      data-testid="sidebar-loader"
    >
      <BaseSpinner
        class="tw:w-10 tw:h-10"
        color="secondary"
      />
    </div>
    <template v-else>
      <div
        :class="{
          'tw:blur-sm tw:pointer-events-none tw:select-none':
            showRestrictMessage,
        }"
        data-testid="blurred-container"
      >
        <div class="outreach-contact-sidebar__body">
          <div
            v-if="
              showFullProfileLink && !showRestrictMessage && selectedContact
            "
            class="tw:flex tw:justify-end"
          >
            <RouterLink
              class="tw:cursor-pointer tw:hover:text-blue-500 tw:hover:underline tw:pt-3"
              :to="{ name: routeName, params: { id: selectedContact.id } }"
            >
              {{ getLocaleText("outreach_contact_sidebar.full_profile") }}
            </RouterLink>
          </div>
          <OutreachContactProfile
            v-if="activeTab === 'about' && selectedContact"
            class="tw:pt-3"
            :contact="selectedContact"
            :outlined="contactOutlined"
            :skip-private-data="showRestrictMessage"
          />

          <OutreachContactRecentArticles
            v-if="activeTab === 'articles' && selectedContact"
            :class="{
              'feature-opaque': outreachFeatures.quickSearch().isDisabled,
            }"
            :contact="selectedContact"
            hide-stats
            :offset-height="headerHeight"
            scroll-target=".outreach-contact-sidebar"
            sticky
          />

          <OutreachContactSocialFeed
            v-if="activeTab === 'feed'"
            class="q-pt-lg"
            :class="{ 'feature-opaque': !hasOutreachActivitiesListsSearch }"
            scroll-target=".outreach-contact-sidebar"
            :twitter-username="twitterUsername"
          />

          <OutreachContactLists
            v-if="activeTab === 'lists' && selectedContact"
            :class="{
              'feature-opaque': outreachFeatures.contactLists().isDisabled,
            }"
            compact
            :contact-id="selectedContact.id"
            :contact-type="contactType"
            data-testid="lists"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useRoute } from "vue-router";

import { outreachFeatures } from "shared/boot/authorization";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseIcon,
  BaseSeparator,
  BaseSpinner,
  BaseTab,
  BaseTabs,
} from "shared/components/base";
import UpsellCard from "shared/components/UpsellCard.vue";
import { type ContactType } from "shared/composables/useContact";
import useOutreachTiers from "shared/composables/useOutreachTiers";
import { useSideBarStore } from "shared/modules/SideBar/stores/useSideBarStore";
import features from "shared/services/features";
import { addShortcutList, removeShortcutList } from "shared/services/shortcuts";
import { useGlobalStore } from "shared/stores/global";
import { useOutreachContactStore } from "shared/stores/outreachContact";
import { AppSection } from "shared/types";

import OutreachContactHeader from "src/components/Outreach/OutreachContactHeader.vue";
import OutreachContactLists from "src/components/Outreach/OutreachContactLists.vue";
import OutreachContactProfile from "src/components/Outreach/OutreachContactProfile.vue";
import OutreachContactRecentArticles from "src/components/Outreach/OutreachContactRecentArticles.vue";
import OutreachContactSocialFeed from "src/components/Outreach/OutreachContactSocialFeed.vue";

interface ShortcutConfig {
  callback: () => void;
  key: string;
}

interface SidebarTab {
  icon: string;
  label: string;
  name: TabName;
  visible?: boolean;
}

type SidebarRouteName = "outreach-search-influencer" | "outreach-search-outlet";
type TabName = "about" | "articles" | "feed" | "lists";

const route = useRoute();

const globalStore = useGlobalStore();
const { isStreem } = storeToRefs(globalStore);

const sideBarStore = useSideBarStore();
const { sideBarData } = storeToRefs(sideBarStore);

const outreachContactStore = useOutreachContactStore();

const { selectedContact, selectedContactResourceType, isContactLoading } =
  storeToRefs(outreachContactStore);

const { hasOutreachActivitiesListsSearch } = useOutreachTiers();

const platformUrl = computed<string>(() =>
  isStreem.value
    ? "https://www.streem.com.au/outreach/"
    : "https://www.cision.com/journalist-outreach/"
);

const hasMatchingContactRoute = computed<boolean>(() => {
  if (selectedContactResourceType.value === "outlet") {
    return (
      route.name === "outreach-search-outlet" &&
      String(selectedContact.value?.id) === String(route.params.id)
    );
  }

  if (selectedContactResourceType.value === "influencer") {
    return (
      route.name === "outreach-search-influencer" &&
      String(selectedContact.value?.id) === String(route.params.id)
    );
  }

  return false;
});

const contactType = computed<string>(
  () => selectedContactResourceType.value ?? ""
);

const showRestrictMessage = computed<boolean>(
  () => !features.has("has_outreach")
);

const isDatabaseMatchesPreview = computed<boolean>(
  () => sideBarData.value?.source === "database_matches"
);

const showFullProfileLink = computed<boolean>(
  () => !hasMatchingContactRoute.value
);

const contactOutlined = computed<boolean>(() => !hasMatchingContactRoute.value);

const routeName = computed<SidebarRouteName>(() =>
  selectedContactResourceType.value === "outlet"
    ? "outreach-search-outlet"
    : "outreach-search-influencer"
);

const header = useTemplateRef<HTMLDivElement>("header");

const activeTab = ref<TabName>("about");

const tabs = computed<SidebarTab[]>(() => [
  {
    name: "about",
    label: getLocaleText("outreach_contact_sidebar.about"),
    icon: "user",
  },
  {
    name: "articles",
    label: getLocaleText("outreach_contact_sidebar.articles"),
    icon: "article",
  },
  {
    name: "feed",
    label: getLocaleText("outreach_contact_sidebar.feed"),
    icon: "x-social",
  },
  {
    name: "lists",
    label: getLocaleText("outreach_contact_sidebar.lists"),
    icon: "list",
  },
]);

const tabsFiltered = computed<SidebarTab[]>(() =>
  tabs.value.filter((tab) => {
    if (tab.visible === false) {
      return false;
    }

    return !(
      selectedContactResourceType.value === "outlet" &&
      ["articles", "feed"].includes(tab.name)
    );
  })
);

const headerHeight = ref<number>(0);

const twitterUsername = computed<string>(() => {
  if (selectedContact.value && "twitter_user" in selectedContact.value)
    return selectedContact.value.twitter_user?.username || "";

  return "";
});

function onClose() {
  sideBarStore.hideSideBar();
}

function backToDatabaseMatches() {
  sideBarStore.setSideBarContent({
    data: {
      loading: sideBarData.value?.loading,
      matches: sideBarData.value?.matches as ContactType[] | undefined,
      onPaginationChange: sideBarData.value?.onPaginationChange,
      pagination: sideBarData.value?.pagination,
    },
    hideHeader: true,
    slotComponent: "OutreachDatabaseMatchesPanel",
    width: "576px",
  });
}

const shortcuts: ShortcutConfig[] = [
  {
    key: "Escape",
    callback: onClose,
  },
];

const isSidebarDisabled = computed<boolean>(() =>
  Boolean(showRestrictMessage.value || isContactLoading.value)
);

watch(
  () => selectedContact.value,
  () => {
    nextTick(() => {
      headerHeight.value = header.value?.clientHeight ?? 0;
      activeTab.value = tabsFiltered.value[0].name;
    });
  },
  { immediate: true }
);

watch(
  () => route.name,
  () => {
    sideBarStore.hideSideBar();
  }
);

onMounted(() => {
  addShortcutList(shortcuts);
});

onBeforeUnmount(() => {
  removeShortcutList(shortcuts);
});
</script>

<style lang="scss" scoped>
:deep(.c-table) {
  --c-table-border-radius: unset;

  border: 1px solid var(--c-table-border-color);
}

:deep(.c-table td) {
  border-right: 1px solid var(--c-table-border-color);
  border-bottom: none;
}

:deep(.form-wrapper-layout__section-condensed) {
  padding: 24px 0 16px;
  border-bottom: none;
}

.feature-opaque {
  opacity: 0.5;
  filter: blur(4px);
  pointer-events: none;
}

:deep(.outreach-contact-profile__stat) {
  margin-inline: -24px;
}

:deep(.outreach-contact-profile-section) {
  margin-inline: -24px;
}

.outreach-contact-sidebar {
  overflow: auto;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-top: 0;

  &__sticky {
    padding-top: 24px;
    background-color: white;
    position: sticky;
    z-index: 500;
    top: 0;
  }

  &__separator {
    margin: 24px -24px 0;
  }

  &__tabs {
    padding-top: 16px;
    margin-inline: -24px;
    border-bottom: 1px solid var(--s-color-denim-2);

    :deep(.tabs) {
      padding-inline: 24px;
      display: flex;
      gap: var(--s-spacing-xl);
    }

    :deep(.tab) {
      .indicator {
        height: 2px;
      }

      .active {
        color: var(--s-color-denim-2);
      }

      .base-button {
        display: flex;
        flex-direction: row;
        gap: var(--s-spacing-md);
        padding-inline: var(--s-spacing-sm);
        padding-top: 0;
        padding-bottom: 16px;
        color: var(--s-color-primary) !important;

        &:hover {
          --c-button-bg-hover: transparent;
          --c-button-border-hover: transparent;
        }
      }

      .base-button:not(.active) {
        color: var(--s-color-denim-7) !important;
      }
    }
  }

  &__database-preview-header {
    margin-inline: -24px;
    padding-inline: 24px;
  }

  &--database-matches-preview {
    .outreach-contact-sidebar__separator {
      margin-inline: 0;
    }

    .outreach-contact-sidebar__tabs {
      margin-inline: 0;

      :deep(.tabs) {
        padding-inline: 0;
      }
    }
  }
}
</style>
